/**
 * The assessment document Zod schema — app-admin's authoring guard.
 *
 * PORTED FROM  `app-student/app/entities/assessment/schema.ts` (plus its six interaction
 *              descriptors under `app-student/app/entities/assessment/interactions/`)
 * COMMIT       1da0a8f01345609aa4160f641e086ca3be5c9f0f
 * AUDIT DRIFT  git -C app-student diff 1da0a8f01345609aa4160f641e086ca3be5c9f0f \
 *                -- app/entities/assessment/schema.ts app/entities/assessment/interactions/
 *
 * There is no fixture corpus and no cross-repo drift test (decision 11), so that SHA is the only
 * handle for auditing drift. A second copy exists because `lib-core-types` stays types-only
 * (decision 2) and the backend holds no schema knowledge (decision 3), so this file is the only
 * validation between an admin's keystrokes and a kid opening a signed link.
 *
 * DELIBERATE OMISSIONS from the port — absent on purpose, not missed:
 *  - NO GRADING. app-student's descriptors carry `grade()`; the admin's preview is read-only
 *    (decision 4), so only the schemas came across.
 *  - NO TOKEN FILLING. `fillTokens` (`[[kid_name]]`) is a player-render concern; the admin authors
 *    the raw token text and the preview shows it exactly as authored.
 *  - NO `idkEnabled`. "I don't know" is offered on every interactive activity (PRD Q11), so authored
 *    definitions carry no flag for it (B2) — do not add it.
 *
 * ADDED here: the rubric catalog and the document-level schema. The player never sees `data.rubrics`,
 * but the admin authors it (decision 7), and the cross-reference refinement needs both halves in one
 * schema.
 *
 * DELIBERATE NARROWING of lib, not drift — do NOT "fix" it (B10): lib keeps
 * `ReviewRubric = ScoredRubric | ObservationRubric` for app-student and app-tutor, while app-admin
 * authors exactly one rubric, so `ReviewRubric` below is that single shape. The accepted consequence
 * is that a stored template whose catalog holds an observation rubric cannot be edited in admin;
 * there is no migration and no compatibility branch, only a legible error (the `levels` message
 * below). See also `./types.ts` and `./schema.conformance.ts`.
 *
 * B10 was amended by B12.1.2, and that amendment is now REVERSED: `standards` left the rubric while
 * a question had exactly one rubric and one set of tags, which made the two lists byte-identical. A
 * question may now carry SEVERAL rubrics, each reviewed against different standards, so
 * `ReviewRubric.standards` is back (codes only). The activity's own array stays, as the AUTO-GRADED
 * question's field.
 *
 * `kind` stays optional. Zod is pinned to the same 4.4.3 as app-student, so the port is near-verbatim.
 */
import { z } from 'zod'

import type { OpenEndedFormat } from '../../model/assessments/types'

import { pluralize, withCount } from '@shared/lib/format'

import { Grade } from '../../model/assessments/enums'

/** Single global monotonic integer. Additive changes are non-breaking; a breaking one is handled by
 *  upcast-on-read in the player. Do NOT bump without bumping app-student's copy in the same breath. */
export const SCHEMA_VERSION = 1

// --- Markers — exported rather than inlined, because the refinements below and the editor's
// annotated textarea must agree on them byte for byte. ----------------------

/** fill_in_blanks: gaps are marked in STORED `text` with this token, in order; `blanks[i]` answers
 *  the i-th gap. What the player grades — not what the author types (see `GAP_TOKEN_REGEX`). */
export const GAP_MARKER = '{{}}'

/** fill_in_blanks, AUTHORING side: the author writes the answer inside the braces —
 *  `7 + {{five}} = {{twelve}}` — and `lib/assignments/fill-in-blanks.ts` splits that into stored `text` (bare
 *  `{{}}`) plus `blanks`. The same regex matches both forms, since `[^{}]*` also matches the empty
 *  answer of a bare `GAP_MARKER`.
 *
 *  `[^{}]*` rather than `.*?` makes an answer containing a brace unauthorable instead of mis-parsed
 *  (`{{\frac{1}{2}}}` would capture `\frac{1`); the accepted cost is that a KaTeX answer cannot be an
 *  inline gap answer.
 *
 *  Global, so use `replace`/`match`/`split` (which ignore `lastIndex`) — never bare `test`/`exec`. */
export const GAP_TOKEN_REGEX = /\{\{([^{}]*)\}\}/g

/** drag_the_words: gaps are inline `*word*` markers in `text` — the marked words are the answer key,
 *  in order. Global, matching the player's split pattern; use `match`/`split`. */
export const DRAG_GAP_REGEX = /\*([^*]+)\*/g

/** Number of `{{}}` gaps in a **stored** fill_in_blanks `text`. */
export const countGapMarkers = (text: string): number => text.split(GAP_MARKER).length - 1

/** Number of gaps in an INLINE authored fill_in_blanks text, where each gap carries its own answer.
 *  Counts bare `{{}}` too, so it agrees with `countGapMarkers` on stored text. */
export const countGapTokens = (text: string): number => text.match(GAP_TOKEN_REGEX)?.length ?? 0

/** Number of `*word*` gaps in a drag_the_words `text`. */
export const countDragGaps = (text: string): number => text.match(DRAG_GAP_REGEX)?.length ?? 0

// --- Content blocks --------------------------------------------------------

export const AssetRef = z.object({
  url: z.string(),
})

// Content blocks — orthogonal to interactions, their own small union. `audioUrl`/`alt`
// ship now (no read-aloud UI yet) to avoid a later migration of stored definitions.
export const TextBlock = z.object({
  type: z.literal('text'),
  value: z.string(), // rich text; math via KaTeX delimiters
  audioUrl: z.string().optional(),
})
export const ImageBlock = z.object({
  type: z.literal('image'),
  asset: AssetRef,
  alt: z.string(),
})
export const AudioBlock = z.object({
  type: z.literal('audio'),
  asset: AssetRef,
})

export const ContentBlock = z.discriminatedUnion('type', [
  TextBlock,
  ImageBlock,
  AudioBlock,
])

// --- Interactions — one z.object per type, combined by a discriminated union; schemas only, the
// graders stayed in app-student. -------------------------------------------

export const Choice = z.object({
  id: z.string(),
  text: z.string(),
  correct: z.boolean(),
})

export const MultipleChoice = z.object({
  type: z.literal('multiple_choice'),
  mode: z.enum(['single', 'multi']),
  options: z.array(Choice).min(2),
})

export const Blank = z.object({
  /** Stays an ARRAY because the player grades off it, but the admin now authors exactly ONE answer
   *  per gap, so everything this repo writes is a single-element array; a stored blank with several
   *  is read as its first and loses the rest on the next save.
   *
   *  No `.min(1)`: an empty `accepted` is caught by refinement 2 with a message naming the gap, and a
   *  type-level failure here would skip every refinement in the document, burying it. */
  accepted: z.array(z.string()),
  /** NO LONGER AUTHORABLE and NO LONGER PRESERVED — the one part of B9 this repo overrides
   *  deliberately. Matching is always case-insensitive and never tolerates a typo, and now that
   *  `blanks` is DERIVED from the authored text on every keystroke, preserving a flag by gap index
   *  would re-attach it to a different gap the moment one is inserted mid-sentence. Still accepted
   *  so a stored document parses; simply dropped on the next save. */
  caseSensitive: z.boolean().optional(),
  acceptSpellingErrors: z.boolean().optional(),
})

export const FillInBlanks = z.object({
  type: z.literal('fill_in_blanks'),
  /** Stored form: gaps are bare `{{}}`. The author never sees this string — the editor shows the
   *  inline `{{answer}}` form and derives this pair from it. */
  text: z.string(),
  /** Positional: `blanks[i]` answers the i-th `{{}}`. Derived from the authored text, never edited
   *  independently. No `.min(1)` — "no gaps at all" is refinement 2's message to give. */
  blanks: z.array(Blank),
})

// `distractors` are extra draggable words that belong in no gap. The draggable bank shown
// to the kid = gap answers + distractors.
export const DragTheWords = z.object({
  type: z.literal('drag_the_words'),
  text: z.string(),
  distractors: z.array(z.string()).optional(),
})

// `backgroundAsset` is inlined as `{ url }` exactly as in app-student (where it avoids an
// import cycle); it matches `AssetRef`.
export const Whiteboard = z.object({
  type: z.literal('whiteboard'),
  backgroundAsset: z.object({ url: z.string() }).optional(),
  backgroundAlt: z.string().optional(),
})

/** The ways a kid may answer an open-ended activity, in the order the player renders its tabs. */
export const OPEN_ENDED_FORMATS = ['text', 'whiteboard', 'photo'] as const

/** What an absent `formats` means: every document authored before the field existed is text-only. */
export const DEFAULT_OPEN_ENDED_FORMATS: readonly OpenEndedFormat[] = ['text']

// The prompt (text/image/audio) is the Activity's `content` blocks — these interactions
// only capture the answer, so they carry no prompt of their own.
export const OpenEnded = z.object({
  type: z.literal('open_ended'),
  /** Order carries no meaning. An empty array is invalid (refinement 9), not "no restriction". */
  formats: z.array(z.enum(OPEN_ENDED_FORMATS)).optional(),
})

export const AudioResponse = z.object({
  type: z.literal('audio_response'),
})

/** Zod's `discriminatedUnion` must *enumerate* its members, so adding an interaction type
 *  means a schema above plus one entry here plus an editor/preview component (T11/T13). */
export const Interaction = z.discriminatedUnion('type', [
  MultipleChoice,
  FillInBlanks,
  DragTheWords,
  Whiteboard,
  OpenEnded,
  AudioResponse,
])

// --- Activity / Screen / Assessment ----------------------------------------

/**
 * NO CATALOGUE VALIDATION on a standard code, deliberately (B12.5.26): it is any string, and the
 * schema never checks it against the `standards` table. The picker's grade filter is hard, so
 * changing a template's grade can strand a code that was legal when authored — and those must still
 * save and load (B12.5.25).
 *
 * The legacy `{ code, label }` object is NOT accepted here: `normalizeAssessmentDocument` converts it
 * to a bare code as the template loads, the only place either shape is known about.
 */

export const Activity = z.object({
  id: z.string(),
  /** What the question is FOR, in the author's words ("Multi-digit addition"). The tutor's review
   *  lists it in place of the prompt, which is the kid's text. Never shown to the kid, never
   *  required — an untitled activity still parses. */
  name: z.string().optional(),
  content: z.array(ContentBlock),
  interaction: Interaction.optional(),
  points: z.number().optional(),
  /** An activity may carry several standards, and a tag may be a broad cluster rather than a leaf —
   *  stored exactly as the author picked it, expanded to leaves only when a mastery map is built,
   *  never at authoring time (B12.2.8).
   *
   *  `.min(1)` is the ONLY validation on this field: removing the last standard deletes the key.
   *  Entries are CODES. This is the AUTO-GRADED question's field — a tutor-reviewed one tags its
   *  standards on its rubrics instead, and nothing forbids a document holding both. */
  standards: z.array(z.string()).min(1).optional(),
  /** **Ids** into the rubric catalog at `data.rubrics` — not the catalog itself
   *  (RND-34 decision B8). Cross-checked against the catalog by the document-level schema. */
  rubrics: z.array(z.string()).optional(),
})

export const ScreenLayout = z.enum(['single', 'two-column'])

// A screen is the sequence unit: shared content plus the questions about it, all visible at once.
// `activities` may be empty — a passive screen, e.g. an intro or a passage on its own.
export const Screen = z.object({
  id: z.string(),
  layout: ScreenLayout.default('single'),
  content: z.array(ContentBlock),
  activities: z.array(Activity),
  /** Overrides the forward button's label, so a narrative assessment can say
   *  "Take the envelope" instead of "Next". Absent ⇒ the player's own wording. */
  cta: z.string().optional(),
  /** Marks the screen whose CTA submits the attempt. Screens after it are a kid-only epilogue shown
   *  once submitted — the tutor never grades them. Absent everywhere ⇒ the final screen submits. */
  isLast: z.boolean().optional(),
}).refine(
  // Refinement 7 — kept verbatim from app-student. Its issue path is the screen itself
  // (`screens.<i>`), which is precise enough for the editor to focus the screen card.
  screen => screen.content.length > 0 || screen.activities.length > 0,
  { message: 'A screen must carry content, activities, or both' },
)

/** The bare object, before the coherence refinements are attached. Not exported — consumers
 *  want `Assessment`. */
const AssessmentObject = z.object({
  id: z.string(),
  schemaVersion: z.number().int(),
  /** The kid-facing name, which is NOT the template row's title: the row is the catalogue handle a
   *  tutor picks from, this is what the story calls itself on the intro screen. Seeded from the row
   *  on create, edited apart from it after that. */
  title: z.string(),
  /** The hook under that title ("Did you know what it takes to become an astronaut?"). Absent
   *  ⇒ the player falls back to its own reassurance about taking your time. */
  description: z.string().optional(),
  disciplineCode: z.enum(['MATH', 'ELA']),
  grade: z.enum(Grade),
  screens: z.array(Screen),
})

/** Internal only: the definition as the un-refined object yields it. Spelled off `AssessmentObject`
 *  so the refinement helpers below do not participate in `Assessment`'s own type inference (which
 *  would be circular). */
type AssessmentShape = z.output<typeof AssessmentObject>
type ActivityShape = AssessmentShape['screens'][number]['activities'][number]

// --- Rubric catalog — `data.rubrics` ---------------------------------------
//
// ONE RUBRIC TYPE (B10). lib's `ReviewRubric` is a union of a scored and an observation variant;
// app-admin authors only the scored one, so there is no union here and no `kind` machinery anywhere
// in the editor. The narrowing is deliberate — see the header.

/** Named after lib's `ScoredRubricLevel`, which app-admin re-exports unchanged. Only the RUBRIC
 *  collapsed to one symbol; the level type keeps lib's name so the two spellings cannot drift. */
export const ScoredRubricLevel = z.object({
  score: z.number(),
  description: z.string().optional(),
})

/**
 * THE rubric — one shape, no union. Structurally lib's `ScoredRubric`, and therefore still a legal
 * lib `ReviewRubric` (asserted in `./schema.conformance.ts`).
 *
 * `kind` stays OPTIONAL (B8: real production rows omit it entirely), so both spellings parse and
 * round-trip byte-identical. It is a `z.literal('scored')`, so `kind: 'observation'` is rejected here
 * as well as by the missing `levels` — two honest issues on the same rubric, both with a path.
 *
 * Both messages are deliberately short: they are two spellings of ONE fact, and B10's explanation
 * lives in `useValidationReport`, which collapses them into one banner per rubric. Keep the paths
 * (`rubrics.<id>.kind`, `rubrics.<id>.levels`) stable — the classifier keys on them.
 */
export const ReviewRubric = z.object({
  id: z.string(),
  kind: z.literal('scored', {
    // Reached by a stored observation rubric, which carries `kind: 'observation'`.
    error: 'This rubric is not a scored rubric, so it cannot be edited here.',
  }).optional(),
  name: z.string(),
  levels: z.array(ScoredRubricLevel, {
    // Decision B10's accepted consequence, made visible: a stored observation rubric has
    // `prompts` and no `levels`.
    error: 'This rubric has no score levels, so it cannot be edited here.',
  }),
  instructions: z.string().optional(),
  /** Which standards a tutor reviews this rubric against — codes, exactly like the activity's array;
   *  the statements are mapped in from the catalogue where they are displayed.
   *
   *  This REINSTATES the field B12.1.2 removed, because its reason no longer holds: back then a
   *  rubric's standards were a byte-exact duplicate of the referencing activity's array, since a
   *  question had exactly one rubric. A question may now carry several, each reviewed against
   *  different standards, and the activity's own array is what an AUTO-GRADED question uses.
   *
   *  `.min(1)`, like the activity's: an emptied list deletes the key. */
  standards: z.array(z.string()).min(1).optional(),
})

/**
 * THE THREE BANDS. Every rubric this editor authors carries exactly these scores, in this order: the
 * editor offers no add, no remove and no reorder, and the score itself is not editable.
 *
 * Deliberately NOT a schema rule. The schema still accepts any `levels` array, because a stored
 * rubric authored before this decision may carry four bands or score them 1–3, and invalidating it
 * would lock an admin out of editing everything else about it. This is the shape the editor WRITES
 * and the slots it RENDERS; the schema stays the wider contract.
 */
export const RUBRIC_LEVEL_SCORES = [0, 1, 2] as const

/** `Record<rubricId, ReviewRubric>`. An empty catalog arrives from the API as `[]` — run
 *  `normalizeAssessmentDocument` (see `./normalize-document`) before parsing. */
export const RubricCatalog = z.record(z.string(), ReviewRubric)

// --- Refinements -----------------------------------------------------------
//
// PATH CONVENTION — every issue carries a precise, index-bearing path so the editor can focus the
// offending field: `screens.2.activities.0.interaction.options` at definition level,
// `definition.screens.2.activities.0.rubrics.1` at document level. The two agree by construction:
// the document schema nests the definition under `definition`, and the hook always parses the
// document, so every path it hands out is document-rooted.

/** The subset of Zod's refinement context these helpers use, spelled out so the helpers
 *  stay plain functions (and testable) instead of closing over Zod internals. */
type AddIssue = (issue: { code: 'custom', message: string, path: (string | number)[] }) => void

/** Refinement 8 — a `schemaVersion` the editor cannot author. An EXACT match is required: a higher
 *  version means the document was written by a newer editor and this one would silently drop the
 *  fields it does not know on the next full-replacement save; a lower one means editing and re-saving
 *  would stamp v(n) edits as authored at v(n-1), since this editor ships no upcaster. An authoring
 *  tool must refuse rather than corrupt. (`SCHEMA_VERSION` is 1, so only "higher" is reachable.) */
const refineSchemaVersion = (definition: AssessmentShape, addIssue: AddIssue): void => {
  if (definition.schemaVersion === SCHEMA_VERSION) {
    return
  }
  addIssue({
    code: 'custom',
    path: ['schemaVersion'],
    message: definition.schemaVersion > SCHEMA_VERSION
      ? `Schema version ${definition.schemaVersion} is newer than this editor supports (${SCHEMA_VERSION}). Update app-admin before editing this template.`
      : `Schema version ${definition.schemaVersion} is older than this editor supports (${SCHEMA_VERSION}). This editor has no upcaster.`,
  })
}

/** Refinement 4a — duplicate `screen.id`. Ids key the player's navigation, so they must be
 *  unique. Reported on every repeat occurrence, at `screens.<i>.id`. */
const refineScreenIds = (definition: AssessmentShape, addIssue: AddIssue): void => {
  const seen = new Set<string>()
  definition.screens.forEach((screen, screenIdx) => {
    if (seen.has(screen.id)) {
      addIssue({
        code: 'custom',
        path: ['screens', screenIdx, 'id'],
        message: `Duplicate screen id "${screen.id}" — screen ids must be unique.`,
      })
      return
    }
    seen.add(screen.id)
  })
}

/** Refinement 4b — duplicate `activity.id`, checked across the WHOLE assessment: responses and
 *  outcomes are keyed by `activityId` alone, so two would collide whichever screens they sit on. */
const refineActivityIds = (definition: AssessmentShape, addIssue: AddIssue): void => {
  const seen = new Set<string>()
  definition.screens.forEach((screen, screenIdx) => {
    screen.activities.forEach((activity, activityIdx) => {
      if (seen.has(activity.id)) {
        addIssue({
          code: 'custom',
          path: ['screens', screenIdx, 'activities', activityIdx, 'id'],
          message: `Duplicate activity id "${activity.id}" — activity ids must be unique across the assessment.`,
        })
        return
      }
      seen.add(activity.id)
    })
  })
}

/** Refinement 6 — `isLast: true` on more than one screen. The flag marks the screen whose CTA
 *  submits the attempt; two is ambiguous. Reported at `screens.<i>.isLast`. */
const refineIsLast = (definition: AssessmentShape, addIssue: AddIssue): void => {
  let found = false
  definition.screens.forEach((screen, screenIdx) => {
    if (screen.isLast !== true) {
      return
    }
    if (found) {
      addIssue({
        code: 'custom',
        path: ['screens', screenIdx, 'isLast'],
        message: 'Only one screen may be marked as the last (submitting) screen.',
      })
      return
    }
    found = true
  })
}

/** Refinements 1–3 and 9 — per-interaction coherence, each pointed at the field that caused it. */
const refineInteraction = (
  activity: ActivityShape,
  basePath: (string | number)[],
  addIssue: AddIssue,
): void => {
  const interaction = activity.interaction
  if (!interaction) {
    return
  }
  const path = (...tail: (string | number)[]): (string | number)[] =>
    [...basePath, 'interaction', ...tail]

  // Refinement 1 — multiple_choice answer key.
  if (interaction.type === 'multiple_choice') {
    const correctCount = interaction.options.filter(option => option.correct).length
    if (correctCount === 0) {
      addIssue({
        code: 'custom',
        path: path('options'),
        message: 'A multiple choice activity needs at least one option marked correct.',
      })
    }
    if (interaction.mode === 'single' && correctCount > 1) {
      addIssue({
        code: 'custom',
        path: path('options'),
        message: `Single-answer mode allows exactly one correct option, but ${correctCount} are marked correct.`,
      })
    }
    return
  }

  // Refinement 2 — fill_in_blanks needs gaps, and every gap needs an answer. This used to be a
  // gap-count-vs-answer-count check, back when the two were authored separately and could drift.
  // `blanks` is now derived from the authored text, one entry per gap, so what is left are the two
  // states an author can still reach: a sentence with no gaps, and a gap whose braces are empty.
  if (interaction.type === 'fill_in_blanks') {
    const gaps = countGapMarkers(interaction.text)

    if (gaps === 0) {
      addIssue({
        code: 'custom',
        path: path('text'),
        message: 'A fill in the blanks activity needs at least one {{answer}} gap in the text.',
      })
    }

    // A TRIPWIRE, not an authoring error: the counts can only disagree if the codec broke, so the
    // message says so rather than asking the author to reconcile two lists they cannot see.
    if (gaps !== interaction.blanks.length) {
      addIssue({
        code: 'custom',
        path: path('blanks'),
        message: `The text has ${gaps} ${pluralize(gaps, { one: 'gap', other: 'gaps' })} but ${withCount(interaction.blanks.length, { one: 'answer key', other: 'answer keys' })} — reopen the activity and re-save; if it persists, this is a bug.`,
      })
    }

    // Path is `text`, NOT `blanks.<i>.accepted`: the report focuses the field an author fixes it in,
    // and the answer now lives in the text — a `blanks` path would point at a field that no longer
    // exists. The position rides in the message instead.
    interaction.blanks.forEach((blank, index) => {
      if (!blank.accepted.some(answer => answer.trim().length > 0)) {
        addIssue({
          code: 'custom',
          path: path('text'),
          message: `Gap ${index + 1} has no answer — type the answer between its braces, as in {{five}}.`,
        })
      }
    })
    return
  }

  // Refinement 3 — drag_the_words needs at least one *word* marker.
  if (interaction.type === 'drag_the_words') {
    if (countDragGaps(interaction.text) === 0) {
      addIssue({
        code: 'custom',
        path: path('text'),
        message: 'A drag the words activity needs at least one *word* marker in the text.',
      })
    }
    return
  }

  // Refinement 9 — open_ended needs at least one answer format. Absent is legal and means text only.
  if (interaction.type === 'open_ended' && interaction.formats?.length === 0) {
    addIssue({
      code: 'custom',
      path: path('formats'),
      message: 'An open ended activity needs at least one answer format — tick Text, Whiteboard or Photo.',
    })
  }
}

/** Every definition-internal refinement, run against an already type-valid definition.
 *  Paths emitted here are definition-rooted. */
const refineDefinition = (definition: AssessmentShape, addIssue: AddIssue): void => {
  refineSchemaVersion(definition, addIssue)
  refineScreenIds(definition, addIssue)
  refineActivityIds(definition, addIssue)
  refineIsLast(definition, addIssue)
  definition.screens.forEach((screen, screenIdx) => {
    screen.activities.forEach((activity, activityIdx) => {
      refineInteraction(
        activity,
        ['screens', screenIdx, 'activities', activityIdx],
        addIssue,
      )
    })
  })
}

/**
 * The assessment DEFINITION — `data.definition`, the half conformant to lib's `Assessment`. Carries
 * the eight refinements that live entirely inside the definition; refinement 5 (rubric
 * cross-reference) needs the catalog and lives on `AssessmentTemplateDocumentSchema` below.
 */
export const Assessment = AssessmentObject.superRefine((definition, ctx) => {
  refineDefinition(definition, issue => ctx.addIssue(issue))
})

/** A validated definition. Spelled off the **exported** schema, so `schema.conformance.ts`
 *  asserts against literally `z.infer<typeof Assessment>`. */
export type ParsedAssessment = z.infer<typeof Assessment>
export type ParsedContentBlock = z.infer<typeof ContentBlock>
export type ParsedReviewRubric = z.infer<typeof ReviewRubric>

/** Refinement 5 — `activity.rubrics: [id]` pointing at an id absent from `data.rubrics`. The one
 *  refinement that crosses the document boundary.
 *
 *  An ABSENT catalog and an EMPTY one behave identically: a silent pass on absent would be the worse
 *  failure, since that is exactly the state a freshly-created template is in and the reference would
 *  rot unnoticed. Path is the offending id itself. */
const refineRubricReferences = (
  document: { definition: AssessmentShape, rubrics?: Record<string, unknown> },
  addIssue: AddIssue,
): void => {
  const known = new Set(Object.keys(document.rubrics ?? {}))
  document.definition.screens.forEach((screen, screenIdx) => {
    screen.activities.forEach((activity, activityIdx) => {
      activity.rubrics?.forEach((rubricId, rubricIdx) => {
        if (known.has(rubricId)) {
          return
        }
        addIssue({
          code: 'custom',
          path: [
            'definition',
            'screens',
            screenIdx,
            'activities',
            activityIdx,
            'rubrics',
            rubricIdx,
          ],
          message: `Unknown rubric id "${rubricId}" — no such rubric in the catalog.`,
        })
      })
    })
  })
}

/**
 * The whole authored document — `data` as the admin-api stores it: `{ definition, rubrics }`. This,
 * not `Assessment`, is what the editor validates, so every path the hook returns is document-rooted.
 *
 * Feed it a document that has already been through `normalizeAssessmentDocument` — the API returns an
 * empty `rubrics` map as `[]` (B7) and `z.record` rejects an array.
 */
export const AssessmentTemplateDocumentSchema = z.object({
  definition: Assessment,
  rubrics: RubricCatalog.optional(),
  /**
   * The media manifest (B13.8, §1) — object paths for the uploads this document references. Declared
   * for the type and for intent, NOT for enforcement: Zod strips unknown keys rather than erroring,
   * and the store is the manifest's only writer.
   *
   * DELIBERATELY UNREFINED. An empty array is legal, a stale entry is legal between an upload and the
   * next save, and a manifest is never a problem the author can act on.
   */
  media: z.array(z.string()).optional(),
}).superRefine((document, ctx) => {
  refineRubricReferences(document, issue => ctx.addIssue(issue))
})

export type ParsedAssessmentTemplateDocument = z.output<typeof AssessmentTemplateDocumentSchema>
